// What the machine is doing, derived from the mirror (live values, state/op, state/care, state/boot) the way the local
// app shows it: the state chip in the header, the alarm, the notes of the machine tab. Pure functions; React renders
// the keys with the text table.
import { liveLook, localTime, presenceOf } from './calm.js';
import type { MachineView } from './store.js';
import { interruptedNow } from './interrupted.js';

export interface Live {
  currentTemp?: number;
  targetTemp?: number;
  heaterPower?: number;
  state?: number;
  brewTime?: number;
  scale?: number;
  weight?: number | null;
  flow?: number | null;
  battery?: number | null;
  warmup?: number;
  pulse?: number;
  pulses?: number;
  held?: boolean;
  sw?: boolean;
  steam?: number;
  pi?: number;
  fp?: boolean;
  bfd?: boolean;
  clean?: number;
  bfc?: number;
  bfn?: number;
  bfp?: number;
  rinse?: boolean;
  dsc?: number;
  dsr?: number;
  dsp?: number;
  dst?: number;
  dsf?: boolean;
  rssi?: number;
}

export interface Op {
  state?: number;
  switch?: boolean;
  held?: boolean;
  steam?: number;
  flushHint?: boolean;
  brew?: { active: boolean; source?: string };
  standby?: { inMin?: number | null; warmMin?: number | null };
  backflush?: {
    armed: boolean;
    cleaner?: number;
    cycle?: number;
    cycles?: number;
    phase?: number;
    done?: boolean;
    by?: string;
    expiresInS?: number | null;
  };
  warmup?: { stage: number; pulse?: number; pulses?: number };
  rinse?: boolean;
  descale?: { phase: number; round?: number; pass?: number; remaining?: number; pump?: boolean };
}

export interface Care {
  today: number;
  week: number;
  total: number;
  coffeeGrams: number;
  waterMl: number;
  descaledAt: number | null;
  backflushAt: number | null;
  shotsSinceBackflush: number;
  dripMl: number;
  due: { descale: boolean; backflush: boolean; drip: boolean };
}

/** Readings of the live values (not state): they stay while the last value is held (core/calm.ts). */
const READINGS = [
  'currentTemp',
  'targetTemp',
  'heaterPower',
  'scale',
  'weight',
  'flow',
  'battery',
  'rssi',
] as const;

/**
 * The live values as the screens use them: the last ones while they are held (at most 60 s, not while the machine is
 * offline, restarting or unknown; core/calm.ts liveLook), null otherwise. When the op group is newer than the held
 * values (they lag over the weak link, op comes with QoS 1), only the readings remain: state, shot, programme and
 * flags then come from op, so a held value never shows a shot or a programme that has ended.
 */
export function liveOf(view: MachineView, now: number): Live | null {
  if (!view.live || liveLook(view, now).look === 'none') return null;
  const data = view.live.data as Live;
  const op = view.groups.op;
  if (!op || op.receivedAt <= view.live.receivedAt) return data;
  const readings: Record<string, unknown> = {};
  for (const k of READINGS) if (k in data) readings[k] = data[k];
  return readings as Live;
}

export const opOf = (view: MachineView): Op => (view.groups.op?.data ?? {}) as Op;
export const careOf = (view: MachineView): Care | null =>
  (view.groups.care?.data as Care | undefined) ?? null;

export type Tone = 'off' | 'ready' | 'heat' | 'brew' | 'water' | 'error' | 'calm';

// Ready as the round display says it (RoundDisplayUi.cpp ready_ with kHysteresis): one rule for the ring of
// "Maschine", the chip over the other tabs and the temperature row of the shot dial (screens/ready.ts). Dominik
// 23.10.2026: "in anderen tabs beim aufheizen steht dran heizt noch obwohl auf der startseite bereits bereit steht"
// (the ring said ready within 1 K, the chip within 0.5 K). The store keeps whether it was ready (MachineView.readyHeld).
/** the display's band without a value of the machine (display.blinking.delta, RoundDisplayModel.h readyBand) */
export const READY_BAND_K = 0.3;
/** once ready, the display keeps saying so up to this much further from the target (kHysteresis) */
export const READY_HOLD_K = 0.3;

export type TempTone = 'ready' | 'heat' | 'cool';

/** Ready within max(band, 0.1) K of the target, once ready until READY_HOLD_K further; below heating, above cooling. */
export function tempTone(temp: number, target: number, band: number, held: boolean): TempTone {
  const d = temp - target;
  if (Math.abs(d) <= Math.max(band, 0.1) + (held ? READY_HOLD_K : 0)) return 'ready';
  return d < 0 ? 'heat' : 'cool';
}

/**
 * What the temperature is compared with: the controller's target, else the machine's setpoint (in standby the target
 * is 0). Just after waking up the first live values can still carry the warm-keeping target of standby (70 °C) while
 * the state already says heating: the ring then showed "70°" for a moment before the setpoint (22.10.2026).
 */
export function readyTarget(
  live: Live | null,
  standby: boolean,
  setpoint: number | undefined,
  warm: number,
): number | undefined {
  const sp = setpoint && setpoint > 0 ? setpoint : undefined;
  const staleWarm = !standby && warm > 0 && live?.targetTemp === warm && sp !== undefined && sp !== warm;
  const liveTarget =
    typeof live?.targetTemp === 'number' && live.targetTemp > 0 && !staleWarm ? live.targetTemp : undefined;
  return liveTarget ?? sp;
}

/** Whether the machine is ready after these live values, given whether it was before (the display's hold). */
export function nextReadyHeld(
  was: boolean,
  live: Live | null,
  o: { setpoint: number | undefined; warm: number; band: number },
): boolean {
  const target = readyTarget(live, false, o.setpoint, o.warm);
  if (typeof live?.currentTemp !== 'number' || target === undefined) return false;
  return tempTone(live.currentTemp, target, o.band, was) === 'ready';
}

export interface Chip {
  key: string;
  vars?: Record<string, string | number>;
  tone: Tone;
}

/** The header chip (stateText() of the local app); offline/restarting from the API's connection state, calm (FR-14). */
export function chipOf(
  view: MachineView,
  now: number,
  opts: { readyBand?: number; piSeconds?: number; tone?: TempTone | null } = {},
): Chip {
  // a short drop keeps the machine's state (core/calm.ts); without a connection of this device the last state shows
  const presence = presenceOf(view, now);
  if (presence === 'restarting') return { key: 'conn_restarting', tone: 'calm' };
  if (presence === 'reconnecting') return { key: 'conn_reconnecting', tone: 'calm' };
  if (presence === 'unknown') return { key: 'conn_unknown', tone: 'off' };
  if (presence === 'offline') return { key: 'chip_offline', tone: 'off' };
  const live = liveOf(view, now);
  const op = opOf(view);
  const s = live?.state ?? op.state;
  const dsc = live?.dsc ?? op.descale?.phase ?? 0;
  if (dsc > 0 && dsc < 7)
    return dsc === 2
      ? { key: 'chip_descale_round', vars: { r: live?.dsr ?? op.descale?.round ?? 0 }, tone: 'water' }
      : { key: 'chip_descale', tone: 'water' };
  if (s === 20) {
    const pi = live?.pi ?? 0;
    const piS = opts.piSeconds ?? 0;
    const t = live?.brewTime ?? 0;
    return {
      key: live?.rinse ? 'chip_flush_s' : pi ? 'chip_pi' : 'chip_brew',
      // seconds with one decimal; the screen writes them in its language (chipView)
      vars: { s: Math.round((pi || live?.rinse ? t : Math.max(0, t - piS)) * 10) / 10 },
      tone: 'brew',
    };
  }
  const bfc = live?.bfc ?? op.backflush?.cycle ?? 0;
  if (s === 50 && bfc > 0)
    return {
      key: 'chip_backflush_n',
      vars: { c: bfc, n: live?.bfn ?? op.backflush?.cycles ?? 0 },
      tone: 'water',
    };
  const pulses = live?.pulses ?? op.warmup?.pulses ?? 0;
  if (s === 25)
    return pulses > 1
      ? { key: 'chip_flush_n', vars: { n: live?.pulse ?? op.warmup?.pulse ?? 0, m: pulses }, tone: 'brew' }
      : { key: 'chip_flush', tone: 'brew' };
  const fixed: Record<number, Chip> = {
    0: { key: 'chip_start', tone: 'off' },
    30: { key: 'chip_steam', tone: 'heat' },
    40: { key: 'chip_water', tone: 'water' },
    50: { key: 'chip_backflush', tone: 'water' },
    60: { key: 'chip_pid_off', tone: 'off' },
    70: { key: 'chip_tank', tone: 'water' },
    80: { key: 'chip_standby', tone: 'off' },
    100: { key: 'chip_overtemp', tone: 'error' },
    110: { key: 'chip_sensor', tone: 'error' },
  };
  if (s !== undefined && fixed[s]) return fixed[s] as Chip;
  const steam = live?.steam ?? op.steam ?? 0;
  if (steam === 1) return { key: 'chip_steam', tone: 'heat' };
  if (steam === 2) return { key: 'chip_steam_cool', tone: 'calm' };
  if (!live || typeof live.currentTemp !== 'number' || typeof live.targetTemp !== 'number')
    return { key: 'chip_unknown', tone: 'off' };
  // the display's rule with its hold (screens/ready.ts readyTone); without it the band alone
  const tone =
    opts.tone ?? tempTone(live.currentTemp, live.targetTemp, opts.readyBand ?? READY_BAND_K, false);
  if (tone === 'ready') return { key: 'chip_ready', tone: 'ready' };
  return tone === 'heat' ? { key: 'chip_heating', tone: 'heat' } : { key: 'chip_cooling', tone: 'calm' };
}

export interface Alarm {
  key: 'alarmOver' | 'alarmSensor' | 'alarmTank' | 'steamHot';
  tone: 'error' | 'info';
}

export function alarmOf(view: MachineView, now: number): Alarm | null {
  const presence = presenceOf(view, now);
  if (presence === 'offline' || presence === 'restarting' || presence === 'unknown') return null;
  const live = liveOf(view, now);
  const s = live?.state ?? opOf(view).state;
  if (s === 100) return { key: 'alarmOver', tone: 'error' };
  if (s === 110) return { key: 'alarmSensor', tone: 'error' };
  if (s === 70) return { key: 'alarmTank', tone: 'info' };
  if (s === 10 && (live?.steam ?? opOf(view).steam) === 2) return { key: 'steamHot', tone: 'info' };
  return null;
}

export type NoteAction = 'rinse' | 'drip' | 'descaleNext';

export interface Note {
  key: string;
  vars?: Record<string, string | number>;
  tone: 'info' | 'water' | 'warn';
  action?: NoteAction;
}

/** Text of the descaling programme (dsText() of the local app); null if it does not run. */
export function descaleNote(op: Op, live: Live | null): Note | null {
  const d = op.descale;
  const phase = live?.dsc ?? d?.phase ?? 0;
  if (phase <= 0 || phase >= 7) return null;
  const left = live?.dst ?? d?.remaining ?? -1;
  const mmss = left >= 0 ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` : '–';
  const round = live?.dsr ?? d?.round ?? 0;
  const pass = live?.dsp ?? d?.pass ?? 0;
  const pump = live?.dsf ?? d?.pump ?? false;
  switch (phase) {
    case 1:
      return { key: 'dsCooling', vars: { c: Math.round(live?.currentTemp ?? 0) }, tone: 'water' };
    case 2:
      return {
        key: 'dsRound',
        vars: { r: round, n: 8, what: pump ? 'dsPumping' : 'dsSoaking', t: mmss },
        tone: 'water',
      };
    case 3:
      return { key: 'dsRefill', tone: 'warn', action: 'descaleNext' };
    case 4:
      return { key: 'dsRest', tone: 'water' };
    case 5:
      return { key: 'dsWaitRinse', vars: { p: pass }, tone: 'warn', action: 'descaleNext' };
    default:
      return { key: 'dsRinse', vars: { p: pass }, tone: 'water' };
  }
}

/**
 * About how many seconds a running backflush still takes (paintFlush() of the local app): the cycles still to come with
 * pump and pause each, the current phase counted from when the machine reported it.
 */
export function backflushLeft(o: {
  cycle: number;
  cycles: number;
  /** 1 pumping, otherwise the pause */
  phase: number;
  fill: number;
  pause: number;
  /** ms since the current cycle and phase began */
  elapsedMs: number;
}): number {
  const rest =
    Math.max(0, o.cycles - o.cycle) * (o.fill + o.pause) + (o.phase === 1 ? o.fill + o.pause : o.pause);
  return Math.max(0, Math.round(rest - o.elapsedMs / 1000));
}

/**
 * "Backflush: Zyklus 2 von 5 · Pumpen · noch ~38 s" while it runs (state 50 with a cycle), else null. The phase began
 * when the op group last reported this cycle and phase (it is sent on every change); live values that are ahead of it
 * count from now.
 */
export function backflushProgressNote(
  view: MachineView,
  now: number,
  times: { fill: number; pause: number },
): Note | null {
  const live = liveOf(view, now);
  const op = opOf(view);
  const bf = op.backflush;
  const state = live?.state ?? op.state;
  const cycle = live?.bfc ?? bf?.cycle ?? 0;
  if (state !== 50 || cycle <= 0) return null;
  const cycles = live?.bfn ?? bf?.cycles ?? 0;
  const phase = live?.bfp ?? bf?.phase ?? 0;
  const opGroup = view.groups.op;
  const sameAsOp = bf?.cycle === cycle && bf.phase === phase && opGroup !== undefined;
  const elapsedMs = sameAsOp ? Math.max(0, now - localTime(view, opGroup.receivedAt)) : 0;
  const left = backflushLeft({ cycle, cycles, phase, fill: times.fill, pause: times.pause, elapsedMs });
  return {
    key: 'bfProgressLeft',
    vars: { c: cycle, n: cycles, p: phase === 1 ? 'bfPumping' : 'bfPause', s: left },
    tone: 'water',
  };
}

/** Starts the local app reports as a note for a while: a crash, the watchdog, a brownout (paintBootNote()). */
const UNPLANNED = ['crash', 'watchdog', 'brownout'];

/** The notes of the machine tab, in the order of the local app. */
export function machineNotes(
  view: MachineView,
  now: number,
  opts: {
    remindAfter: number;
    dripCapacity: number;
    watertank: boolean;
    crashWithinMs?: number;
    /** backflush.fill_time and backflush.flush_time for the time left (defaults of the local app 5 s and 10 s) */
    backflushTimes?: { fill: number; pause: number };
  },
): Note[] {
  if (!view.ready && view.updatedAt === 0) return [];
  const live = liveOf(view, now);
  const op = opOf(view);
  const care = careOf(view);
  const out: Note[] = [];
  const bf = op.backflush;
  const cleaner = live?.clean ?? bf?.cleaner ?? 0;
  if (bf?.armed && cleaner === 0 && !(bf.cycle && bf.cycle > 0) && !bf.done)
    out.push({ key: bf.by ? 'bfActiveBy' : 'bfActive', vars: { by: bf.by ?? '' }, tone: 'info' });
  if (cleaner > 0) out.push({ key: `clean${cleaner}`, tone: 'water' });
  const state = live?.state ?? op.state;
  const progress = backflushProgressNote(view, now, opts.backflushTimes ?? { fill: 5, pause: 10 });
  if (progress) out.push(progress);
  if (live?.bfd ?? bf?.done) out.push({ key: 'bfDoneNote', tone: 'info' });
  if (care?.due.drip && opts.dripCapacity > 0)
    out.push({ key: 'dripDue', vars: { ml: care.dripMl }, tone: 'water', action: 'drip' });
  const ds = descaleNote(op, live);
  if (ds) out.push(ds);
  if (care?.due.descale)
    out.push({ key: 'descaleDue', vars: { l: Math.round(care.waterMl / 1000) }, tone: 'info' });
  if (care?.due.backflush && opts.remindAfter > 0)
    out.push({ key: 'bfDue', vars: { n: care.shotsSinceBackflush }, tone: 'info' });
  const boot = view.groups.boot;
  const cause = (boot?.data as { cause?: string } | undefined)?.cause;
  if (boot && cause && UNPLANNED.includes(cause)) {
    const at = localTime(view, boot.receivedAt);
    const minutes = Math.max(1, Math.round((now - at) / 60_000));
    if (now - at < (opts.crashWithinMs ?? 60 * 60_000))
      out.push(
        cause === 'crash'
          ? { key: 'bootCrash', vars: { m: minutes }, tone: 'warn' }
          : {
              key: cause === 'brownout' ? 'bootBrownout' : 'bootWatchdog',
              vars: { m: minutes },
              tone: 'warn',
            },
      );
  }
  const cut = interruptedNow(view.interrupted, view.groups.op, now);
  if (cut) out.push({ key: cut.key, tone: 'warn' });
  if ((live?.warmup ?? op.warmup?.stage) === 1)
    out.push({ key: care?.due.drip && opts.dripCapacity > 0 ? 'flushWaitsDrip' : 'flushNote', tone: 'info' });
  const fp = live?.fp ?? op.flushHint ?? false;
  // not while it flushes (the flush is the rinse; 22.10.2026 the card asked to rinse during the flush)
  if (fp && state !== 20 && state !== 25 && !(live?.bfd ?? bf?.done))
    out.push({ key: 'rinseNote', tone: 'info', ...(opts.watertank ? { action: 'rinse' as const } : {}) });
  return out;
}

/** "an · Standby in n min" / "Standby · warm x °C, aus in t" / "aus" (paintPills() of the local app). */
export function heatingText(
  view: MachineView,
  pidEnabled: boolean,
  warmTemp: number,
): { key: string; vars?: Record<string, string | number> } {
  const op = opOf(view);
  if (op.state === 80) {
    const warm = op.standby?.warmMin;
    if (typeof warm === 'number' && warm > 0)
      return { key: 'heatStandbyWarm', vars: { c: warmTemp, t: duration(warm) } };
    return { key: 'heatStandby' };
  }
  if (!pidEnabled) return { key: 'heatOff' };
  const inMin = op.standby?.inMin;
  // within a window of the schedule the minutes run to its off time (firmware standby.h): "Standby in 1 h 44 min"
  return typeof inMin === 'number'
    ? { key: 'heatOnStandby', vars: { t: duration(inMin) } }
    : { key: 'heatOn' };
}

export const duration = (min: number): string =>
  min >= 60 ? `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}` : `${min} min`;

/** WLAN quality in words (good from -67 dBm, fair from -75 dBm). */
export function wifiQuality(rssi: number | undefined): 'wifiGood' | 'wifiOk' | 'wifiWeak' | null {
  if (typeof rssi !== 'number' || rssi >= 0) return null;
  return rssi >= -67 ? 'wifiGood' : rssi >= -75 ? 'wifiOk' : 'wifiWeak';
}
